// ./src/hooks/useTopUpDrawer.ts
//
// Everything needed to open, drive and close a top-up purchase — the WIRING around
// `TopUpDrawer`, in one place.
//
// ⚠ THIS FILE EXISTS BECAUSE THE COPYABLE PART OF A FEATURE IS NEVER THE COMPONENT. When the
// event dashboard needed the same purchase flow the composer already had, `TopUpDrawer` itself
// was trivially reusable — it is pure presentation. What was NOT reusable was the fifty lines of
// glue around it: pick a bundle, remember which one so its price can be shown on the pay button,
// create the intent, poll after payment, reset on close, reload the balance. Copying THAT into a
// second caller would have forked the flow that takes money, and the two copies would have
// drifted the first time either was touched.
//
// ⚠ ONE HOOK INSTANCE, deliberately. `useTopUp` is called here and nowhere else in a caller, so
// the balance the drawer shows and the balance the page shows are the same object. Two instances
// would each hold their own copy and disagree the moment one of them reloaded.
//
// ⚠ READING IS EVENT-RELATIVE; BUYING IS ACCOUNT-WIDE, ALWAYS (ROADMAP A2.9.2). The balance sums
// this event's credits, the account's, and the allowance, in the order a send spends them. A
// PURCHASE no longer chooses — it lands on the account and is spendable on any event, so both
// callers buy the same thing and the drawer has no scope to disclose.
import { useCallback, useMemo, useState } from "react";
import { formatWithCurrencyLabel } from "@em/utils";

import { creditFeatureForKind, resolveTopUpKind, TopUpKind } from "@/utils/topUp";
import { MessagingBalance, useTopUp } from "@/hooks/useTopUp";
import type { TopUpDrawerProps } from "@/components/features/Billing/TopUpDrawer";
import { refusalReason } from "@/utils";

export interface UseTopUpDrawerParams {
  /** The event whose balance is read. Required — the balance op is event-scoped by design. */
  eventId?: string | null;
  /**
   * ⚠ WHICH OF THE TWO COUNTERS. There is no third: SMS and WhatsApp both spend
   * `message_credits`, so a caller with a channel maps it here rather than passing it on.
   */
  kind: TopUpKind;
  /** True when opened from inside a task the host would hate to lose (the composer). */
  inTask?: boolean;
  /**
   * #186 — read the wallet and the balance when the drawer OPENS rather than when the caller mounts: for a page that
   * shows neither (the event dashboard), every visit paid two reads for a menu item rarely opened.
   */
  readOnOpen?: boolean;
}

export interface UseTopUpDrawerReturn {
  /** The spending power for this kind, for a cost line outside the drawer. */
  balance: MessagingBalance | undefined;
  balanceLoaded: boolean;
  reloadBalance: () => Promise<void>;
  /**
   * Open the drawer and load its bundles.
   *
   * ⚠ Takes an OPTIONAL kind so one hook instance can serve a caller that sells both counters
   * (the event dashboard's menu). Two hook instances would each mount their own balance read
   * and fire two identical queries on every page load, then disagree the moment one reloaded.
   */
  open: (nextKind?: TopUpKind) => void;
  /** Open the SERVICES catalog instead of a credit top-up. */
  openServices: () => void;
  /** Everything `<TopUpDrawer>` needs. Spread it. */
  drawerProps: TopUpDrawerProps;
}

export const useTopUpDrawer = (params: UseTopUpDrawerParams): UseTopUpDrawerReturn => {
  const { eventId, kind, inTask = false, readOnOpen = false } = params;

  const topUp = useTopUp(eventId, { readOnMount: !readOnOpen });
  const [opened, setOpened] = useState(false);
  /**
   * The bundle the host has PICKED but not yet bought.
   *
   * ⚠ PICKING AND BUYING ARE TWO STEPS NOW, and that is the whole shape of this drawer. Each
   * bundle used to carry its own Buy button, which meant the discount field underneath could
   * never take part — pressing Buy on a row committed before the host had reached the code. So
   * the rows select, the code is typed, and ONE primary action in the footer does the buying.
   */
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  /**
   * Which counter the OPEN drawer is selling. Null means "whatever the caller's current `kind`
   * is".
   *
   * ⚠ Derived rather than stored-and-synced, deliberately. The composer's kind follows the
   * channel the host picked, so a copy held in state would go stale the moment they switched
   * from email to SMS without reopening the drawer — and the drawer would then sell the wrong
   * counter, leaving the send exactly as blocked as before. Clearing this on close makes the
   * fallback self-correcting instead of something to remember to re-sync.
   */
  const [openedKind, setOpenedKind] = useState<TopUpKind | null>(null);
  /**
   * The discount code the host typed, if any.
   *
   * ⚠ HELD HERE RATHER THAN IN THE DRAWER so it survives the drawer's own step changes (choose
   * a bundle → pay → back), and so "Back" from the payment step returns to a list that still
   * remembers the code. It is cleared on close with everything else.
   *
   * ⚠ NOTHING IS VALIDATED HERE. The code is sent with the purchase and the SERVER decides —
   * a client-side "looks like a code" check would only ever disagree with the authority.
   */
  const [discountCode, setDiscountCode] = useState("");
  /**
   * Which catalog the open drawer is showing.
   *
   * ⚠ ONE HOOK INSTANCE SERVES BOTH, deliberately. A second `useTopUpDrawer` for services would
   * mount its own `useTopUp`, fire a second identical account-credit read on every page load,
   * and then disagree with the first the moment either reloaded — the exact reason this file
   * exists as one instance in the first place.
   */
  const [mode, setMode] = useState<"credits" | "services">("credits");

  const drawerKind = openedKind ?? kind;

  // ⚠ The CREDIT feature, not the allowance one. `email_qty` is the monthly allowance and
  // cannot be bought; `email_credits` is the bundle. Asking the catalog for the wrong one
  // returns an empty list, which renders as "no bundles available" — a dead end that looks like
  // a server problem rather than a wrong argument.

  const open = useCallback(
    (nextKind?: TopUpKind) => {
      // ⚠ VALIDATED, NOT TRUSTED — and this guard is standing on a bug that was actually
      // written. This callback is the natural thing to hand straight to a Button's `onClick`
      // (`onTopUp={topUp.open}`), and React then calls it with a CLICK EVENT as `nextKind`.
      // `event ?? kind` keeps the event, `event === "email"` is false, and the drawer silently
      // loads MESSAGE bundles for an email send — leaving the host exactly as blocked, with a
      // plausible-looking list of the wrong thing.
      //
      // ⚠ `tsc` cannot catch it: the consuming props are typed `() => void`, and a function
      // taking an optional argument is assignable to that. So the check has to be at runtime,
      // here, once — rather than as a rule every future caller has to remember.
      const k = resolveTopUpKind(nextKind, kind);

      topUp.reset();
      setDiscountCode("");
      setSelectedSku(null);
      setMode("credits");
      // ⚠ `k`, not the state that is about to be set. `setOpenedKind` does not take effect
      // until the next render, so loading from state here would fetch the PREVIOUS counter's
      // bundles — the classic version of this bug, and one that shows up as a correct-looking
      // list of the wrong thing.
      topUp.loadBundles({ featureCode: creditFeatureForKind(k) });

      if (readOnOpen) {
        void topUp.reloadBalance();
      }

      setOpenedKind(k);
      setOpened(true);
    },
    [topUp, kind, readOnOpen],
  );

  /**
   * Open the SERVICES catalog — a call, a setup, concierge hours.
   *
   * ⚠ Takes no argument, so it is safe to hand straight to a Button's `onClick` (which would
   * otherwise pass a click event). `open` needs `resolveTopUpKind` for exactly that reason;
   * this one sidesteps it by having nothing to get wrong.
   */
  const openServices = useCallback(() => {
    topUp.reset();
    setDiscountCode("");
    setSelectedSku(null);
    setMode("services");
    topUp.loadBundles({ addonType: "SERVICE" });

    if (readOnOpen) {
      void topUp.reloadBalance();
    }

    setOpened(true);
  }, [topUp, readOnOpen]);

  const close = useCallback(() => {
    setOpened(false);
    setOpenedKind(null);
    setSelectedSku(null);
    setDiscountCode("");
    setMode("credits");
    topUp.reset();
    // ⚠ The balance may have MOVED while the drawer was open — that is the whole point of
    // having opened it. Whatever the caller shows outside reads from it.
    void topUp.reloadBalance();
  }, [topUp]);

  const chosenBundle = useMemo(
    () => topUp.bundles.find((b) => b.providerSku === selectedSku) ?? null,
    [topUp.bundles, selectedSku],
  );

  const amountLabel = useMemo(() => {
    if (chosenBundle?.amount == null || !chosenBundle.currency) {
      return null;
    }

    // ⚠ THE DISCOUNTED FIGURE WINS, AND THIS IS THE NUMBER ON THE PAY BUTTON. The bundle's own
    // `amount` is the LIST price; once the server has accepted a code, charging one figure while
    // the button promises another is a money-display bug in the worst possible place. The
    // server's `amountDue` is the authority because the server is what Stripe will charge.
    const due = topUp.discount ? topUp.discount.amountDue : chosenBundle.amount;

    // It says WHICH currency ("US$29.00", never a bare "$"), and this is the number about to be charged.
    return formatWithCurrencyLabel(
      new Intl.NumberFormat(undefined, { style: "currency", currency: chosenBundle.currency }),
      due,
      chosenBundle.currency,
    );
  }, [chosenBundle, topUp.discount]);

  // ⚠ For the CALLER's own cost line, outside the drawer — still event-relative, because what a
  // send can draw on is the allowance plus credits, not the wallet alone.
  const balance = topUp.balance(kind);

  const drawerProps: TopUpDrawerProps = {
    opened,
    onClose: close,
    kind: drawerKind,
    mode,
    inTask,
    bundles: topUp.bundles,
    loadingBundles: topUp.loadingBundles,
    // ⚠ The PURCHASED figure comes from the account wallet, not from the event-relative balance.
    // Same number for email and message (verified against live data), and the only source that
    // covers AI — which the event-scoped op does not report at all, so it showed nothing.
    // ⚠ Only meaningful for a CREDIT top-up. In services mode the drawer sells things whose balance
    // is "how many you have bought", not a spendable credit figure, and showing the message/email
    // balance beside a service would be a number about something else entirely.
    purchasedCredits: mode === "services" ? undefined : topUp.purchasedCredits(drawerKind),
    phase: topUp.phase,
    arrivedCredits: topUp.arrivedCredits,
    errorText: topUp.errorText,
    // ⚠ The refusal reason from the last failed purchase START (usually a discount code). The
    // server's own wording, not a generic fallback, because it names which rule failed.
    purchaseError: topUp.lastError ? refusalReason(topUp.lastError) || null : null,
    clientSecret: topUp.intent?.clientSecret,
    publishableKey: topUp.intent?.publishableKey,
    amountLabel,
    discountCode,
    onDiscountCodeChange: setDiscountCode,
    discount: topUp.discount,
    selectedSku,
    onSelect: setSelectedSku,
    /**
     * The footer's one primary action.
     *
     * ⚠ Guarded on a selection rather than trusting the button's `disabled`. The footer button
     * is disabled without one, but a disabled prop is a UI state, not a rule — and this is the
     * call that takes money.
     */
    onBuy: () => {
      if (!selectedSku) {
        return;
      }

      void topUp.startPurchase(selectedSku, discountCode);
    },
    // ⚠ Stripe reporting success means the MONEY is taken, not that the credits exist — the
    // webhook grants them a moment later. This polls until the balance MOVES rather than
    // assuming, and a timeout is reported as "slow", never as a failure.
    onPaid: () => {
      // ⚠ THE CHOSEN PRODUCT'S FEATURE, not the drawer's kind. A kind only names the three credit
      // features; a SERVICE grants `event_setup` / `quick_start_call` / `concierge_hours`, so
      // passing a kind made every service purchase poll a balance that could never move and report
      // "taking a little longer than usual" forever. Falls back to the kind's feature only when the
      // catalog row somehow carries no code.
      void topUp.onPaid(chosenBundle?.featureCode || creditFeatureForKind(drawerKind));
    },
    onError: topUp.setPaymentError,
    onBack: () => {
      // ⚠ `reset` clears the accepted discount, which is right: going back means re-pricing.
      // The typed code AND the selected bundle deliberately survive (both live here, not in
      // `topUp`), so stepping back returns the host to exactly what they had chosen rather than
      // to an empty form they have to fill in again.
      topUp.reset();
      topUp.loadBundles(
        mode === "services" ? { addonType: "SERVICE" } : { featureCode: creditFeatureForKind(drawerKind) },
      );
    },
  };

  return {
    balance,
    balanceLoaded: topUp.balanceLoaded,
    reloadBalance: topUp.reloadBalance,
    open,
    openServices,
    drawerProps,
  };
};
